import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import { AppButton } from "../ui";
import { Clock } from "lucide-react";

const MODES = ["Online", "In-person"];

const ROUND_OPTIONS = [
  "HR Screening",
  "Technical Round 1",
  "Technical Round 2",
  "Coding Challenge",
  "Managerial Round",
  "Offer Extended",
];

const STATUS_OPTIONS = [
  "Scheduled",
  "Completed",
  "Cancelled",
  "Ongoing",
  "Rescheduled",
  "Passed",
  "Failed",
];

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

const initialState = {
  round: "",
  date: "",
  time: "",
  meetingLink: "",
  duration: "60",
  mode: "",
  status: "Scheduled",
  notes: "",
  interviewer: "",
  forceCreate: false,
};

/** Format "14:30" → "2:30 PM" */
function formatTime12h(time) {
  if (!time) return time;
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

/** Format "2026-02-10" → "Feb 10, 2026" */
function formatDateShort(dateStr) {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

const AddEditRoundModal = ({
  show,
  onHide,
  onSave,
  mode = "add",
  initialData = null,
  loading = false,
  interviewId = null,
  studentId = null,
}) => {
  const [form, setForm] = useState(initialData || initialState);
  const [instructors, setInstructors] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [nextSlot, setNextSlot] = useState(null);

  useEffect(() => {
    if (show) {
      const firstInterviewer = initialData?.interviewers?.[0];
      setForm(initialData ? {
        ...initialState,
        ...initialData,
        duration: initialData.duration || "60",
        interviewer: (firstInterviewer?._id || firstInterviewer || ""),
      } : initialState);
      setConflicts([]);
      setShowConflicts(false);
      setSaveError("");
      setNextSlot(null);
    }
  }, [initialData, show]);

  // Fetch instructors for the multi-select
  useEffect(() => {
    if (!show) return;
    apiClient.get('/instructors/list')
      .then(res => {
        const list = res.data.instructors || res.data || [];
        setInstructors(Array.isArray(list) ? list : []);
      })
      .catch(() => setInstructors([]));
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Reset conflicts when scheduling params change
    if (name === 'date' || name === 'time' || name === 'duration' || name === 'interviewer') {
      setShowConflicts(false);
      setConflicts([]);
      setNextSlot(null);
    }
  };

  const handleCheckAvailability = useCallback(async () => {
    if (!form.date || !form.time || !form.duration) return;
    setCheckingAvail(true);
    setNextSlot(null);
    try {
      const params = {
        date: form.date,
        startTime: form.time,
        duration: parseInt(form.duration) || 60,
      };
      // Include interviewer if selected
      if (form.interviewer) {
        params.interviewerIds = form.interviewer;
      }
      // Include student for student-side conflict check
      if (studentId) {
        params.studentId = studentId;
      }
      const { data } = await apiClient.get('/calendar/availability/bulk', { params });
      setConflicts(data.conflicts || []);
      setShowConflicts(true);
      if (data.nextAvailableSlot) {
        setNextSlot(data.nextAvailableSlot);
      }
    } catch (err) {
      console.error('Availability check error:', err);
    }
    setCheckingAvail(false);
  }, [form.date, form.time, form.duration, form.interviewer, studentId]);

  const handleUseNextSlot = () => {
    if (!nextSlot) return;
    setForm((prev) => ({
      ...prev,
      date: nextSlot.date,
      time: nextSlot.startTime,
    }));
    setShowConflicts(false);
    setConflicts([]);
    setNextSlot(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError("");
    setNextSlot(null);
    const { interviewer, ...rest } = form;
    const payload = {
      ...rest,
      interviewers: interviewer ? [interviewer] : [],
      forceCreate: form.forceCreate || false,
    };
    try {
      await onSave(payload);
    } catch (err) {
      if (err?.response?.status === 409) {
        const errData = err.response.data;
        setConflicts(errData.conflicts || []);
        setShowConflicts(true);
        if (errData.nextAvailableSlot) {
          setNextSlot(errData.nextAvailableSlot);
        }
        setSaveError("Scheduling conflicts detected. Use the suggested slot or force override.");
      } else {
        setSaveError(err?.response?.data?.message || "Failed to save round");
      }
    }
  };

  // Build a readable name for a conflict participant
  const getParticipantLabel = (c) => {
    if (c.userName) return c.userName;
    if (c.userType === 'student') return 'Student';
    return `Interviewer`;
  };

  const canCheckAvailability = form.date && form.time && form.duration;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "edit" ? "Edit Round" : "Add Round"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="mb-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Round Name</Form.Label>
                <Form.Select
                  name="round"
                  value={form.round}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select round</option>
                  {ROUND_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  required
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Time</Form.Label>
                <Form.Control
                  type="time"
                  name="time"
                  value={form.time}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Duration (minutes)</Form.Label>
                <Form.Select
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select duration</option>
                  {DURATION_OPTIONS.map((d) => (
                    <option key={d} value={d}>{d} min</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Mode of interview</Form.Label>
                <Form.Select
                  name="mode"
                  value={form.mode}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Mode</option>
                  {MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            
            <Col md={12}>
              <Form.Group>
                <Form.Label>Interview Link</Form.Label>
                <Form.Control
                  name="meetingLink"
                  value={form.meetingLink}
                  onChange={handleChange}
                  placeholder="https://meet.example.com/your-meeting-link"
                type="url"
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Interviewer Selection */}
          <Row className="mb-2">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Interviewer</Form.Label>
                <Form.Select
                  name="interviewer"
                  value={form.interviewer}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Interviewer</option>
                  {instructors.map((inst) => (
                    <option key={inst._id} value={inst._id}>
                      {inst.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Check Availability – shown when date + time + duration are set */}
          {canCheckAvailability && (
            <Row className="mb-2">
              <Col>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleCheckAvailability}
                  disabled={checkingAvail}
                >
                  {checkingAvail
                    ? <><Spinner size="sm" className="me-1" /> Checking availability...</>
                    : <><Clock size="1em" className="me-1" /> Check Availability</>
                  }
                </Button>
              </Col>
            </Row>
          )}

          {/* Conflict Details */}
          {showConflicts && conflicts.length > 0 && (
            <Row className="mb-2">
              <Col>
                <Alert variant="warning" className="mb-0">
                  <Alert.Heading className="h6 mb-2">Scheduling Conflicts</Alert.Heading>
                  {conflicts.map((participant, pi) => (
                    <div key={pi} className="mb-2">
                      <strong className="text-capitalize">{getParticipantLabel(participant)}</strong>
                      <span className="text-muted small ms-1">({participant.userType})</span>
                      <ul className="mb-1 ps-3 small">
                        {participant.conflicts.map((evt, ei) => (
                          <li key={ei}>
                            {evt.title || evt.companyName || 'Busy'}{' '}
                            — {formatTime12h(evt.startTime)} to {formatTime12h(evt.endTime)}
                            {evt.companyName && <span className="text-muted"> ({evt.companyName})</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  {/* Next available slot suggestion */}
                  {nextSlot && (
                    <div className="d-flex align-items-center gap-2 mt-2 p-2 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                      <Clock size="1em" className="text-success" />
                      <span className="small">
                        <strong>Next available:</strong>{' '}
                        {formatDateShort(nextSlot.date)} at {formatTime12h(nextSlot.startTime)} – {formatTime12h(nextSlot.endTime)}
                      </span>
                      <Button
                        variant="success"
                        size="sm"
                        className="ms-auto"
                        onClick={handleUseNextSlot}
                      >
                        Use this slot
                      </Button>
                    </div>
                  )}

                  <hr className="my-2" />
                  <Form.Check
                    type="checkbox"
                    label="Force schedule anyway (override conflicts)"
                    checked={form.forceCreate || false}
                    onChange={(e) => setForm(prev => ({ ...prev, forceCreate: e.target.checked }))}
                    className="small"
                  />
                </Alert>
              </Col>
            </Row>
          )}
          {showConflicts && conflicts.length === 0 && (
            <Row className="mb-2">
              <Col>
                <Alert variant="success" className="mb-0 small">
                  All participants are available at this time.
                </Alert>
              </Col>
            </Row>
          )}

          {saveError && (
            <Row className="mb-2">
              <Col>
                <Alert variant="danger" className="mb-0 small">{saveError}</Alert>
              </Col>
            </Row>
          )}

          <Row className="mb-2">

            <Col md={12}>
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <AppButton type="submit" disabled={loading}>
            {loading ? (
              <Spinner size="sm" />
            ) : mode === "edit" ? (
              "Save Changes"
            ) : (
              "Add Round"
            )}
          </AppButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddEditRoundModal;
